import { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import * as d3 from 'd3';
import ContentLoader from 'react-content-loader';
import MarketShareDropdown from '../market-dropdown/MarketShareDropdown';
import DonutChart from './donutChart';

import './MarketDonutChart.scss';

const MarketDonutChart = ({ data, vendorNames, vendorColors, availableDates, selectedDate, selectedVendor, onFocusChange }) => {
    const { t, ready } = useTranslation();

    const chartRef = useRef(null);
    const [svgWidth, setSvgWidth] = useState(0);
    const [svgHeight, setSvgHeight] = useState(0);

    const [focusProduct, setFocusProduct] = useState();

    useEffect(() => {
        function resize() {
            if (chartRef.current) {
                setSvgWidth(parseFloat(d3.select(chartRef.current).style('width')));
                setSvgHeight(parseFloat(d3.select(chartRef.current).style('height')));
            }
        }

        window.addEventListener('resize', resize);
        const timeout = setTimeout(() => resize(), 100);

        return (() => {
            window.removeEventListener('resize', resize);
            clearTimeout(timeout);
        });
    }, [chartRef, data, selectedVendor, selectedDate]);

    const vendorColor = useMemo(() => vendorColors && vendorColors[selectedVendor], [vendorColors, selectedVendor]);

    const products = useMemo(() => {
        let pointIndex = data?.findIndex((point) => point.dateHumanized === selectedDate);
        if (pointIndex >= 0) {
            const vendor = data[pointIndex].vendors.find((retailer) => retailer.name === selectedVendor);
            if (vendor) {
                const colors = d3.scaleLinear().domain([0, vendor.products.length + 1]).range([vendorColor, 'white']);
                return vendor.products
                    .sort((p1, p2) => {
                        if (p1.devicePercentage > p2.devicePercentage) {
                            return -1;
                        } else if (p1.devicePercentage < p2.devicePercentage) {
                            return 1;
                        }

                        return 0;
                    })
                    .map((product, index) => ({ ...product, color: colors(index) }));
            }
        }
    }, [data, selectedVendor, vendorColor, selectedDate]);

    const productInfoDom = useMemo(() => {
        if (!products) {
            const textHeight = 20;
            const chartLength = 7 + Math.floor(Math.random() * 6);
            const height = chartLength * textHeight * 2;

            return <ContentLoader
                height={height}
                width={'100%'}
                speed={1}
                className='chartLoader'
                viewBox={`0 0 100 ${height}`}
                style={{ height: height }}
            >
                {Array(chartLength).fill('').map((v, index) => {
                    return <rect key={index} x={0} y={index * textHeight * 2} rx='0' ry='0' width={`${50 + Math.random() * 45}%`} height={textHeight}/>;
                })}
            </ContentLoader>;
        }

        return <table className='products'>
            <tbody>{
                products.map((product) => <tr key={product.name} onMouseEnter={() => setFocusProduct(product)} onMouseLeave={() => setFocusProduct(undefined)}>
                    <th>
                        <div className='productColorBox d-inline-block' style={{ backgroundColor: product.color }}/>
                    </th>
                    <th className='productName d-flex'>
                        <p className={classnames({ 'text-bold': product.name === focusProduct?.name || (focusProduct?.isOther && product.devicePercentage === 0) })}>
                            {product.name}
                        </p>
                    </th>
                    <th className='productPercentage'>
                        <p className={classnames({ 'text-bold': product.name === focusProduct?.name || (focusProduct?.isOther && product.devicePercentage === 0) })}>
                            {(product.devicePercentage * 100).toFixed(2).replace(/(\.00)$|(0)$/, '')}%
                        </p>
                    </th>
                </tr>)}
            </tbody>
        </table>;
    }, [products, vendorColor, focusProduct]);

    if (!ready) {
        return null;
    }

    return <div className='marketDonutChart'>
        <Row>
            <Col md={6}>
                <div className='dropdownWrap'>
                    <MarketShareDropdown title={t('Vendor')} items={vendorNames} selected={selectedVendor} onChange={(name) => onFocusChange(name)}/>
                    <MarketShareDropdown title={t('Month')} items={availableDates} selected={selectedDate} onChange={(date) => onFocusChange(undefined, date)}/>
                </div>
                {productInfoDom}
            </Col>
            <Col md={6}>
                <div className='chartBox' ref={chartRef}>
                    <DonutChart vendorName={selectedVendor} products={products} width={svgWidth} height={svgHeight} focusProduct={focusProduct} onFocusChange={(product) => setFocusProduct(product)}/>
                </div>
            </Col>
        </Row>
    </div>;
};


MarketDonutChart.propTypes = {
    data: PropTypes.array,
    vendorNames: PropTypes.array,
    vendorColors: PropTypes.object,
    availableDates: PropTypes.array,
    selectedVendor: PropTypes.string,
    selectedDate: PropTypes.string,
    onFocusChange: PropTypes.func
};

export default MarketDonutChart;
